import React from "react"
import { Box, Card, CardContent, Theme, makeStyles } from "@material-ui/core"
import { useSnackbar } from "notistack"
import {
  useGetHourReportAdminAssignmentOptionsQuery,
  useUpdateHourReportAdminsMutation,
} from "../../../memberships/api"
import type { HourReportAdmin, HourReportAdminAssignmentOption } from "../../../memberships/api/schemas"
import { errorToMessage } from "../../../utils/errors"
import AdminOwnHoursReviewSettingCheckbox from "../hour-report-settings/AdminOwnHoursReviewSettingCheckbox"
import { AdminInfoBanner } from "./components/AdminInfoBanner"
import { CurrentAdminsPanel } from "./components/CurrentAdminsPanel"
import { useTranslations } from "./hooks/use-translations"
import type { HourReportingWorkflowSettings } from "../../../hour-reports/hooks/use-hour-reporting-workflow-settings"

type HourReportingAdminsProps = {
  orgId: string
  admins: HourReportAdmin[]
  canUpdateAdmins: boolean
  workflowSettings: HourReportingWorkflowSettings
}

const HourReportingAdmins = ({ orgId, admins, canUpdateAdmins, workflowSettings }: HourReportingAdminsProps) => {
  const classes = useStyles()
  const translations = useTranslations(workflowSettings.workflow)
  const { enqueueSnackbar } = useSnackbar()
  const {
    data: assignmentOptions = [],
    isLoading: isLoadingOptions,
    isError: isOptionsError,
  } = useGetHourReportAdminAssignmentOptionsQuery(orgId, { skip: !canUpdateAdmins })
  const [updateAdmins, updateAdminsMeta] = useUpdateHourReportAdminsMutation()
  const [selectedOption, setSelectedOption] = React.useState<HourReportAdminAssignmentOption | null>(null)

  const availableOptions = React.useMemo(() => {
    const adminIds = new Set(admins.map((admin) => admin.id))
    return assignmentOptions.filter((option) => !option.isHourReportAdmin && !adminIds.has(option.id))
  }, [admins, assignmentOptions])

  React.useEffect(() => {
    if (selectedOption && !availableOptions.some((option) => option.id === selectedOption.id)) {
      setSelectedOption(null)
    }
  }, [availableOptions, selectedOption])

  const submitUpdate = async (adminMembershipIdsToAdd: string[], adminMembershipIdsToRemove: string[]) => {
    if (!canUpdateAdmins || updateAdminsMeta.isLoading) return false

    try {
      await updateAdmins({ orgId, adminMembershipIdsToAdd, adminMembershipIdsToRemove }).unwrap()
      return true
    } catch (error) {
      enqueueSnackbar(errorToMessage(error, translations.updateAdminsErrorText, translations), { variant: "error" })
      return false
    }
  }

  const handleAdd = async () => {
    if (!selectedOption) return
    const optionToAdd = selectedOption
    setSelectedOption(null)
    const succeeded = await submitUpdate([optionToAdd.id], [])
    if (!succeeded) setSelectedOption(optionToAdd)
  }

  const handleRemove = (admin: HourReportAdmin) => {
    void submitUpdate([], [admin.id])
  }

  return (
    <div className={classes.root}>
      <AdminInfoBanner translations={translations} />
      {!workflowSettings.isReportingOnlyWorkflow && (
        <Box mt={3}>
          <Card variant="outlined">
            <CardContent>
              <AdminOwnHoursReviewSettingCheckbox />
            </CardContent>
          </Card>
        </Box>
      )}
      <CurrentAdminsPanel
        admins={admins}
        options={availableOptions}
        selectedOption={selectedOption}
        canUpdateAdmins={canUpdateAdmins}
        isUpdating={updateAdminsMeta.isLoading}
        isLoadingOptions={isLoadingOptions}
        isOptionsError={isOptionsError}
        translations={translations}
        onSelectedOptionChange={setSelectedOption}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />
    </div>
  )
}

export default HourReportingAdmins

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    maxWidth: 720,
    marginLeft: "auto",
    marginRight: "auto",
  },
}))
